import { KEYLOCKER_COMMUNICATION_EVENT_TYPE, ROLES } from "models";
import { ACTIVITY_LOG_TYPE } from "modules/AppointmentDetails/components/ActivityLog/utils";

import type { TFunction } from "i18next";
import type {
  ACSES_COMMUNICATION_EVENT_TYPES,
  AcsesCommunicationEvent,
  Appointment,
  CHECK_EVENT_TYPE,
  COMMUNICATION_EVENTS,
  CommunicationEvent,
  DESK_COMMUNICATION_EVENTS,
  DeskCommunicationEvent,
  KIOSK_COMMUNICATION_EVENT_TYPES,
  KeyLockerCommunicationEvent
} from "models";
import type { ActivityLogs } from "modules/AppointmentDetails/components/ActivityLog/utils";
import type { InclusiveUnion } from "utils/type";

export type EVENT_TYPES =
  | COMMUNICATION_EVENTS
  | CHECK_EVENT_TYPE
  | DESK_COMMUNICATION_EVENTS
  | KEYLOCKER_COMMUNICATION_EVENT_TYPE
  | KIOSK_COMMUNICATION_EVENT_TYPES
  | ACSES_COMMUNICATION_EVENT_TYPES;

export const getName = (event: InclusiveUnion<CommunicationEvent | DeskCommunicationEvent | KeyLockerCommunicationEvent | AcsesCommunicationEvent>) => {
  if (event.user) {
    return `${event.user.first_name} ${event.user.last_name}`.trim();
  } else if (event.receivers?.length) {
    return event.receivers[0].destination;
  } else {
    return "";
  }
};

const getKeylockerCommEventNote = (event: KeyLockerCommunicationEvent, t: TFunction) => {
  const placeholderValues = { keylocker_box_number: String(event.keylocker_box_number), keylocker_name: String(event.keylocker_name) };

  if (event.type === KEYLOCKER_COMMUNICATION_EVENT_TYPE.CheckInAnswered)
    return `${t("v8_key_dropped_in_box_BoxNumber_of_KeylockerName", "Key dropped in box {{keylocker_box_number}} of {{keylocker_name}}", { ...placeholderValues })}`;

  if (event.type === KEYLOCKER_COMMUNICATION_EVENT_TYPE.PickUpPINSent)
    return `${t("v8_pick_up_pin_sent_for_box_BoxNumber_of_KeylockerName", "Pick up pin sent for box {{keylocker_box_number}} of {{keylocker_name}}", { ...placeholderValues })}`;

  return null;
};

export const getKeylockerNonEventActivityTitle = (t: TFunction, roleId?: number | null, isCustomer?: boolean) => {
  if (isCustomer) return t("v8_keylocker_open_by_customer", "Keylocker open by customer");

  switch (roleId) {
    case ROLES.Mechanic:
      return t("v8_keylocker_open_by_mechanic", "Keylocker open by mechanic");
    case ROLES.Receptionist:
      return t("v8_keylocker_open_by_receptionist", "Keylocker open by receptionist");
    default:
      return "";
  }
};

export const getKeylockerCommEventLogs = (appointment: Appointment, event: KeyLockerCommunicationEvent, t: TFunction): ActivityLogs[] => {
  const { key_picked_up_by, key_dropped_back_at, key_picked_up_at, key_picked_up_back_at, key_picked_up_back_by, key_dropped_back_by } = appointment;

  const tableData: ActivityLogs[] = [];

  tableData.push({
    time: event.created_on,
    activityType: ACTIVITY_LOG_TYPE.Keylocker,
    name: getName(event),
    note: getKeylockerCommEventNote(event, t),
    eventType: event.type,
    keylockerPinRequestType: event.pin_request_type ?? undefined
  });

  if (event.type === KEYLOCKER_COMMUNICATION_EVENT_TYPE.CheckInAnswered && key_picked_up_at) {
    tableData.push({
      time: key_picked_up_at,
      activityType: ACTIVITY_LOG_TYPE.Keylocker,
      note: t("v8_key_picked_up", "Key picked up"),
      name: key_picked_up_by ? `${key_picked_up_by.first_name} ${key_picked_up_by.last_name}`.trim() : "",
      activityTitle: getKeylockerNonEventActivityTitle(t, key_picked_up_by?.role_id)
    });
  } else if (event.type === KEYLOCKER_COMMUNICATION_EVENT_TYPE.PickUpPINSent) {
    if (key_dropped_back_at) {
      tableData.push({
        time: key_dropped_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        note: t("v8_keylocker_key_dropped_back", "Key dropped back"),
        name: key_dropped_back_by ? `${key_dropped_back_by.first_name} ${key_dropped_back_by.last_name}`.trim() : "",
        activityTitle: getKeylockerNonEventActivityTitle(t, key_dropped_back_by?.role_id)
      });
    }

    if (key_picked_up_back_at) {
      let name = "",
        activityTitle = "";

      if (!key_picked_up_back_by) {
        const KeylockerOpenedByCustomer = true;
        const NonUserRoleId = null;

        activityTitle = getKeylockerNonEventActivityTitle(t, NonUserRoleId, KeylockerOpenedByCustomer);
        name = getName(event);
      } else {
        name = `${key_picked_up_back_by.first_name} ${key_picked_up_back_by.last_name}`.trim();
        activityTitle = getKeylockerNonEventActivityTitle(t, key_picked_up_back_by.role_id);
      }

      tableData.push({
        time: key_picked_up_back_at,
        activityType: ACTIVITY_LOG_TYPE.Keylocker,
        note: t("v8_key_picked_up_back", "Key picked up back"),
        activityTitle,
        name
      });
    }
  }

  return tableData;
};
